<script>
import { ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';
// FORK_PATCH_BEGIN: conversation-counsel
import CounselAutoReplyStatus from 'custom/dashboard/extensions/counsel/components/CounselAutoReplyStatus.vue';
import CounselAutoReplyBusyBanner from 'custom/dashboard/extensions/counsel/components/CounselAutoReplyBusyBanner.vue';
// FORK_PATCH_END: conversation-counsel

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
    // FORK_PATCH: conversation-counsel
    CounselAutoReplyStatus,
    CounselAutoReplyBusyBanner,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    // FORK_PATCH_BEGIN: messenger-human-agent-window
    aiActionsDisabled: {
      type: Boolean,
      default: false,
    },
    // FORK_PATCH_END: messenger-human-agent-window
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div class="flex flex-col">
    <div
      class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
    >
      <EditorModeToggle
        :mode="mode"
        :disabled="disabled"
        :is-reply-restricted="isReplyRestricted"
        @toggle-mode="handleModeToggle"
      />
      <div class="flex items-center mx-4 my-0">
        <div v-if="isMessageLengthReachingThreshold" class="text-xs">
          <span :class="charLengthClass">
            {{ characterLengthWarning }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <!-- FORK_PATCH_BEGIN: conversation-counsel — auto-reply switch on the right (REQ-20260812-11) -->
        <CounselAutoReplyStatus
          v-if="conversationId"
          :conversation-id="conversationId"
        />
        <!-- FORK_PATCH_END: conversation-counsel -->
        <template v-if="captainTasksEnabled && !aiActionsDisabled">
          <div class="relative">
            <NextButton
              ghost
              :disabled="disabled || isEditorDisabled"
              :class="{
                'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
                'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
              }"
              sm
              icon="i-ph-sparkle-fill"
              @click="toggleCopilotMenu"
            />
            <CopilotMenuBar
              v-if="showCopilotMenu"
              v-on-click-outside="handleClickOutside"
              :has-selection="false"
              :editor-content="editorContent"
              :conversation-id="conversationId"
              class="ltr:right-0 rtl:left-0 bottom-full mb-2"
              @execute-copilot-action="handleCopilotAction"
            />
          </div>
          <NextButton
            ghost
            class="text-n-slate-11"
            sm
            icon="i-lucide-maximize-2"
            @click="$emit('toggleEditorSize')"
          />
        </template>
      </div>
    </div>
    <!-- FORK_PATCH_BEGIN: conversation-counsel — busy strip under the switch row (REQ-20260812-10) -->
    <CounselAutoReplyBusyBanner
      v-if="conversationId"
      :conversation-id="conversationId"
    />
    <!-- FORK_PATCH_END: conversation-counsel -->
  </div>
</template>
